import { getTextField } from '@lender/utils';
import { compose } from 'redux';

import { COMMON_DATE_FORMAT } from 'constants/date';
import { FieldInputType } from 'constants/fields';
import { FieldType } from 'constants/formGenerator';
import { MiCompanyType, MiProductType } from 'constants/loanApplication';
import { MAX_PERCENTAGE_VALUE } from 'constants/number';
import {
  miApplicationTypeOptions,
  miCompanyTypeOptions,
  miDecisionTypeOptions,
  miOrderStatusOptions,
  miPaymentTypeOptions,
  miProductTypeOptions,
  miRenewalTypeOptions,
} from 'constants/options';
import { getConditionalArray } from 'utils/data';
import { fillConfigFieldsWithKeys } from 'utils/loanManagement';

import { PropertyAndLoanTestIds } from '../../../../PropertyAndLoan.testIds';

import {
  MI_AMOUNT_PLACEHOLDER,
  MI_MONTHS_PLACEHOLDER,
  MI_PREMIUM_RATE_PERCENT_DECIMAL_SCALE,
  MI_RATE_PLACEHOLDER,
  MI_TEXT_MAX_LENGTH,
  MI_UPFRONT_PREMIUM_PERCENT_DECIMAL_SCALE,
} from './MortgageInsurance.constants';
import { withReadOnlyOrderOutcome, withStoredValueGuards } from './MortgageInsurance.utils';

const getMiDateField = ({ fieldId, label, dataTestId }) => ({
  fieldId,
  type: FieldType.DATE,
  nameInModel: fieldId,
  inputProps: {
    type: FieldInputType.DATE,
    label,
    required: false,
    variant: 'outlined',
    format: COMMON_DATE_FORMAT,
    dataTestId,
  },
});

const getMiTextField = ({ fieldId, label, dataTestId }) => ({
  fieldId,
  type: FieldType.INPUT,
  nameInModel: fieldId,
  inputProps: {
    label,
    placeholder: label,
    required: false,
    maxLength: MI_TEXT_MAX_LENGTH,
    dataTestId,
  },
});

const getMiSelectField = ({ fieldId, label, placeholder = label, options, dataTestId }) => ({
  fieldId,
  type: FieldType.SELECT,
  nameInModel: fieldId,
  inputProps: {
    options,
    label,
    placeholder,
    required: false,
    disableClearable: true,
    withSortedOptions: false,
    dataTestId,
  },
});

const getMiPremiumRateField = ({ fieldId, label, dataTestId }) => ({
  fieldId,
  type: FieldType.INPUT,
  nameInModel: fieldId,
  inputProps: {
    type: FieldInputType.PERCENTAGE_RESTYLED,
    label,
    placeholder: MI_RATE_PLACEHOLDER,
    required: false,
    minValue: 0,
    maxValue: MAX_PERCENTAGE_VALUE,
    decimalScale: MI_PREMIUM_RATE_PERCENT_DECIMAL_SCALE,
    decimalSeparator: '.',
    dataTestId,
  },
});

const getMiTermMonthsField = ({ fieldId, label, dataTestId }) => ({
  fieldId,
  type: FieldType.INPUT,
  nameInModel: fieldId,
  inputProps: {
    type: FieldInputType.INT,
    label,
    placeholder: MI_MONTHS_PLACEHOLDER,
    required: false,
    minValue: 0,
    dataTestId,
  },
});

const getMiAmountField = ({ fieldId, label, dataTestId }) => ({
  fieldId,
  type: FieldType.INPUT,
  nameInModel: fieldId,
  inputProps: {
    type: FieldInputType.CURRENCY,
    label,
    placeholder: MI_AMOUNT_PLACEHOLDER,
    required: false,
    minValue: 0,
    dataTestId,
  },
});

const mortgageInsuranceConfigFields = {
  miProductType: {
    fieldId: 'miProductType',
    type: FieldType.SELECT,
    nameInModel: 'miProductType',
    inputProps: {
      options: miProductTypeOptions,
      placeholder: 'Product Type',
      label: 'Product Type',
      required: false,
      disableClearable: true,
      dataTestId: PropertyAndLoanTestIds.MORTGAGE_INSURANCE_FORM_PRODUCT_TYPE_SELECT,
    },
  },
  miPaymentType: {
    fieldId: 'miPaymentType',
    type: FieldType.SELECT,
    nameInModel: 'miPaymentType',
    inputProps: {
      options: miPaymentTypeOptions,
      placeholder: 'Payment Type',
      label: 'Payment Type',
      required: false,
      disableClearable: true,
      dataTestId: PropertyAndLoanTestIds.MORTGAGE_INSURANCE_FORM_PAYMENT_TYPE_SELECT,
    },
  },
  miRenewalType: {
    fieldId: 'miRenewalType',
    type: FieldType.SELECT,
    nameInModel: 'miRenewalType',
    inputProps: {
      options: miRenewalTypeOptions,
      placeholder: 'Renewals',
      label: 'Renewals',
      required: false,
      disableClearable: true,
      dataTestId: PropertyAndLoanTestIds.MORTGAGE_INSURANCE_FORM_RENEWAL_TYPE_SELECT,
    },
  },
  miRefundable: {
    fieldId: 'miRefundable',
    type: FieldType.CHECKBOX,
    nameInModel: 'miRefundable',
    inputProps: {
      label: 'Refundable',
      required: false,
      dataTestId: PropertyAndLoanTestIds.MORTGAGE_INSURANCE_FORM_REFUNDABLE_CHECKBOX,
    },
  },
  miFinanced: {
    fieldId: 'miFinanced',
    type: FieldType.CHECKBOX,
    nameInModel: 'miFinanced',
    inputProps: {
      label: 'Financed MI',
      required: false,
      dataTestId: PropertyAndLoanTestIds.MORTGAGE_INSURANCE_FORM_FINANCED_CHECKBOX,
    },
  },
  miUpfrontPremiumPercent: {
    fieldId: 'miUpfrontPremiumPercent',
    type: FieldType.INPUT,
    nameInModel: 'miUpfrontPremiumPercent',
    inputProps: {
      type: FieldInputType.PERCENTAGE_RESTYLED,
      placeholder: 'Upfront Premium %',
      label: 'Upfront Premium %',
      required: true,
      decimalScale: MI_UPFRONT_PREMIUM_PERCENT_DECIMAL_SCALE,
      decimalSeparator: '.',
      dataTestId: PropertyAndLoanTestIds.MORTGAGE_INSURANCE_FORM_UPFRONT_PREMIUM_PERCENT_INPUT,
    },
  },
  miCompanyType: getMiSelectField({
    fieldId: 'miCompanyType',
    label: 'MI Company',
    options: miCompanyTypeOptions,
    dataTestId: PropertyAndLoanTestIds.MORTGAGE_INSURANCE_FORM_COMPANY_TYPE_SELECT,
  }),
  miCompanyOtherDescription: {
    fieldId: 'miCompanyOtherDescription',
    type: FieldType.INPUT,
    nameInModel: 'miCompanyOtherDescription',
    inputProps: {
      label: 'Other MI Company Name',
      placeholder: 'Other MI Company Name',
      required: true,
      maxLength: MI_TEXT_MAX_LENGTH,
      customSchema: () => getTextField({ isRequired: true }).max(MI_TEXT_MAX_LENGTH),
      dataTestId: PropertyAndLoanTestIds.MORTGAGE_INSURANCE_FORM_COMPANY_OTHER_DESCRIPTION_INPUT,
    },
  },
  miApplicationType: getMiSelectField({
    fieldId: 'miApplicationType',
    label: 'Application Type',
    options: miApplicationTypeOptions,
    dataTestId: PropertyAndLoanTestIds.MORTGAGE_INSURANCE_FORM_APPLICATION_TYPE_SELECT,
  }),
  miOrderStatus: getMiSelectField({
    fieldId: 'miOrderStatus',
    label: 'Order Status',
    placeholder: 'Not ordered',
    options: miOrderStatusOptions,
    dataTestId: PropertyAndLoanTestIds.MORTGAGE_INSURANCE_FORM_ORDER_STATUS_SELECT,
  }),
  miDecisionType: getMiSelectField({
    fieldId: 'miDecisionType',
    label: 'Decision',
    options: miDecisionTypeOptions,
    dataTestId: PropertyAndLoanTestIds.MORTGAGE_INSURANCE_FORM_DECISION_TYPE_SELECT,
  }),
  miOrderedDate: getMiDateField({
    fieldId: 'miOrderedDate',
    label: 'Ordered Date',
    dataTestId: PropertyAndLoanTestIds.MORTGAGE_INSURANCE_FORM_ORDERED_DATE_INPUT,
  }),
  miDecisionDate: getMiDateField({
    fieldId: 'miDecisionDate',
    label: 'Decision Date',
    dataTestId: PropertyAndLoanTestIds.MORTGAGE_INSURANCE_FORM_DECISION_DATE_INPUT,
  }),
  miActivatedDate: getMiDateField({
    fieldId: 'miActivatedDate',
    label: 'Activated Date',
    dataTestId: PropertyAndLoanTestIds.MORTGAGE_INSURANCE_FORM_ACTIVATED_DATE_INPUT,
  }),
  miCertificateIdentifier: getMiTextField({
    fieldId: 'miCertificateIdentifier',
    label: 'Certificate Number',
    dataTestId: PropertyAndLoanTestIds.MORTGAGE_INSURANCE_FORM_CERTIFICATE_IDENTIFIER_INPUT,
  }),
  miCertificateExpirationDate: getMiDateField({
    fieldId: 'miCertificateExpirationDate',
    label: 'Certificate Expiration Date',
    dataTestId: PropertyAndLoanTestIds.MORTGAGE_INSURANCE_FORM_CERTIFICATE_EXPIRATION_DATE_INPUT,
  }),
  miQuoteIdentifier: getMiTextField({
    fieldId: 'miQuoteIdentifier',
    label: 'Quote ID',
    dataTestId: PropertyAndLoanTestIds.MORTGAGE_INSURANCE_FORM_QUOTE_IDENTIFIER_INPUT,
  }),
  miQuoteExpirationDate: getMiDateField({
    fieldId: 'miQuoteExpirationDate',
    label: 'Quote Expiration Date',
    dataTestId: PropertyAndLoanTestIds.MORTGAGE_INSURANCE_FORM_QUOTE_EXPIRATION_DATE_INPUT,
  }),
  miInitialPremiumRatePercent: getMiPremiumRateField({
    fieldId: 'miInitialPremiumRatePercent',
    label: 'Initial Premium Rate %',
    dataTestId: PropertyAndLoanTestIds.MORTGAGE_INSURANCE_FORM_INITIAL_PREMIUM_RATE_PERCENT_INPUT,
  }),
  miInitialPremiumTermMonths: getMiTermMonthsField({
    fieldId: 'miInitialPremiumTermMonths',
    label: 'Initial Premium Term (Months)',
    dataTestId: PropertyAndLoanTestIds.MORTGAGE_INSURANCE_FORM_INITIAL_PREMIUM_TERM_MONTHS_INPUT,
  }),
  miRenewalPremiumRatePercent: getMiPremiumRateField({
    fieldId: 'miRenewalPremiumRatePercent',
    label: 'Renewal Premium Rate %',
    dataTestId: PropertyAndLoanTestIds.MORTGAGE_INSURANCE_FORM_RENEWAL_PREMIUM_RATE_PERCENT_INPUT,
  }),
  miRenewalPremiumTermMonths: getMiTermMonthsField({
    fieldId: 'miRenewalPremiumTermMonths',
    label: 'Renewal Premium Term (Months)',
    dataTestId: PropertyAndLoanTestIds.MORTGAGE_INSURANCE_FORM_RENEWAL_PREMIUM_TERM_MONTHS_INPUT,
  }),
  miUpfrontPremiumAmount: getMiAmountField({
    fieldId: 'miUpfrontPremiumAmount',
    label: 'Upfront Premium Amount',
    dataTestId: PropertyAndLoanTestIds.MORTGAGE_INSURANCE_FORM_UPFRONT_PREMIUM_AMOUNT_INPUT,
  }),
  miPremiumTaxAmount: getMiAmountField({
    fieldId: 'miPremiumTaxAmount',
    label: 'Premium Tax Amount',
    dataTestId: PropertyAndLoanTestIds.MORTGAGE_INSURANCE_FORM_PREMIUM_TAX_AMOUNT_INPUT,
  }),
};

export const mortgageInsuranceSectionFieldsKeys = Object.keys(mortgageInsuranceConfigFields);

export const getMortgageInsuranceConfig = compose(
  fillConfigFieldsWithKeys,
  ({
    miProductType,
    miCompanyType,
    onCompanyTypeChange,
    storedValues,
    isOrderOutcomeReadOnly = false,
  } = {}) => ({
    groups: withReadOnlyOrderOutcome(isOrderOutcomeReadOnly, [
      {
        type: FieldType.GROUP,
        key: 'mortgageInsurance',
        fields: [
          mortgageInsuranceConfigFields.miProductType,
          mortgageInsuranceConfigFields.miPaymentType,
          mortgageInsuranceConfigFields.miRenewalType,
          mortgageInsuranceConfigFields.miRefundable,
          mortgageInsuranceConfigFields.miFinanced,
          ...getConditionalArray(
            miProductType === MiProductType.SPLIT_PREMIUM,
            withStoredValueGuards(storedValues, [
              mortgageInsuranceConfigFields.miUpfrontPremiumPercent,
            ]),
          ),
        ],
      },
      {
        type: FieldType.GROUP,
        key: 'mortgage-insurance-order',
        groupTitle: { label: 'MI Order' },
        fields: [
          {
            ...mortgageInsuranceConfigFields.miCompanyType,
            inputProps: {
              ...mortgageInsuranceConfigFields.miCompanyType.inputProps,
              customOnChange: onCompanyTypeChange,
            },
          },
          ...getConditionalArray(miCompanyType === MiCompanyType.OTHER, [
            mortgageInsuranceConfigFields.miCompanyOtherDescription,
          ]),
          mortgageInsuranceConfigFields.miApplicationType,
          mortgageInsuranceConfigFields.miOrderStatus,
          mortgageInsuranceConfigFields.miDecisionType,
          ...withStoredValueGuards(storedValues, [
            mortgageInsuranceConfigFields.miOrderedDate,
            mortgageInsuranceConfigFields.miDecisionDate,
            mortgageInsuranceConfigFields.miActivatedDate,
          ]),
        ],
      },
      {
        type: FieldType.GROUP,
        key: 'mortgage-insurance-certificate',
        groupTitle: { label: 'Certificate' },
        fields: [
          mortgageInsuranceConfigFields.miCertificateIdentifier,
          ...withStoredValueGuards(storedValues, [
            mortgageInsuranceConfigFields.miCertificateExpirationDate,
          ]),
        ],
      },
      {
        type: FieldType.GROUP,
        key: 'mortgage-insurance-quote',
        groupTitle: { label: 'Quote' },
        fields: [
          mortgageInsuranceConfigFields.miQuoteIdentifier,
          ...withStoredValueGuards(storedValues, [
            mortgageInsuranceConfigFields.miQuoteExpirationDate,
          ]),
        ],
      },
      {
        type: FieldType.GROUP,
        key: 'mortgage-insurance-premium-schedule',
        groupTitle: { label: 'Premium Schedule' },
        fields: withStoredValueGuards(storedValues, [
          mortgageInsuranceConfigFields.miInitialPremiumRatePercent,
          mortgageInsuranceConfigFields.miInitialPremiumTermMonths,
          mortgageInsuranceConfigFields.miRenewalPremiumRatePercent,
          mortgageInsuranceConfigFields.miRenewalPremiumTermMonths,
          mortgageInsuranceConfigFields.miUpfrontPremiumAmount,
          mortgageInsuranceConfigFields.miPremiumTaxAmount,
        ]),
      },
    ]),
  }),
);
